import React from 'react';
import Sheet from '../common/Sheet';
import './liveStyle.css';

/**
 * TileActionsSheet — mobile ⋯ tile-actions bottom sheet (#86bb5dx62 L-D1/L-D2/L-D3).
 *
 * The mobile equivalent of the desktop right-click CameraContextMenu (there is no
 * right-click on touch). Opened by the ⋯ button at each tile's top-right; mirrors
 * Java dview's per-slot menu (VideoController.java:1211) with the actions lite can
 * honor today:
 *   Snapshot     — capture the tile's current frame  (Java VideoController.java:1141)
 *   Name & Timestamp — global name+time overlay toggle (overlayPrefs.info)  (Java VideoController.java:1116)
 *   Assign / Clear — reuse the existing slot-assignment flow (assign already works)
 *
 * Presentation only — the parent (VideoViewWs) owns the state and wires each handler.
 */
const Icon = ({ children }) => (
  <span className="live-tile-action__ic" aria-hidden="true">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">{children}</svg>
  </span>
);

const TileActionsSheet = ({
  visible,
  slotIndex,
  camera,
  anchor,
  infoOn,
  metaOn,
  onSnapshot,
  onSnapshotAll,
  onAssign,
  onClear,
  onToggleInfo,
  onToggleMeta,
  onClose,
  isCloud = false,
}) => {
  if (!visible) return null;

  const slotNum = (slotIndex != null ? slotIndex : 0) + 1;
  const hasCamera = !!(camera && camera.enabled);
  const camName = hasCamera ? (camera.sName || camera.camName || '') : '';
  // Title = camera number — name (matches the tile OSD), no "Slot" prefix (David). Empty cell → slot.
  const camNum = (camera && camera.bID != null) ? camera.bID : slotNum;
  const title = camName ? `${camNum} — ${camName}` : `Slot ${slotNum}`;

  const actions = (
    <>
      <div className="live-sheet__title">{title}</div>

      {/* Per-cell Snapshot: BENCHED for eView/dView (cross-origin JPEG taints canvas — #86baz05kd).
          Cloud uses WebSocket <canvas> (same-origin), so snapshot works there. */}
      {isCloud && hasCamera && (
          <button className="live-tile-action" onClick={onSnapshot}>
            <Icon><path d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15a5 5 0 110-10 5 5 0 010 10z" /></Icon>
            <span>Snapshot</span>
          </button>
        )}

      {isCloud && (
          <button className="live-tile-action" onClick={onSnapshotAll}>
            <Icon><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></Icon>
            <span>Snapshot all</span>
          </button>
        )}

        {/* Name & Timestamp toggle — the camera name+time overlay (overlayPrefs.info). On desktop the
            overlays gear ⚙ / mobile overlays sheet own overlay visibility, so this is hidden there via
            CSS (David #86bb5dx62). "Camera Info" now names the FPS/bitrate readout (Java parity). */}
        <button className="live-tile-action live-tile-action--info" onClick={onToggleInfo}>
          <Icon><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm0-7a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" /></Icon>
          <span>{infoOn ? 'Hide name & timestamp' : 'Show name & timestamp'}</span>
          <span className="live-tile-action__sub">All tiles</span>
        </button>

        {/* Camera Info toggle — the FPS/bitrate/codec/resolution readout (overlayPrefs.metadata). Global
            (all tiles), the SAME flag the desktop overlays gear ⚙ / Options own — this is just a second,
            closer entry point (David 2026-08-16). Distinct class from --info so it stays visible in the
            desktop popover too (the --info name+time toggle is CSS-hidden on desktop). */}
        <button className="live-tile-action live-tile-action--meta" onClick={onToggleMeta}>
          <Icon><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" /></Icon>
          <span>{metaOn ? 'Hide camera info' : 'Show camera info'}</span>
          <span className="live-tile-action__sub">All tiles</span>
        </button>

        <button className="live-tile-action" onClick={onAssign}>
          <Icon><path d="M4 6h16v2H4V6zm0 5h10v2H4v-2zm0 5h16v2H4v-2zm14-5v3h-2v-3h-3v-2h3V9h2v2h3v2h-3z" /></Icon>
          <span>Assign camera…</span>
        </button>

        {hasCamera && (
          <button className="live-tile-action live-tile-action--danger" onClick={onClear}>
            <Icon><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></Icon>
            <span>Clear slot</span>
          </button>
        )}

        <button className="live-tile-action" onClick={onClose}>
          <Icon><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></Icon>
          <span>Cancel</span>
        </button>
    </>
  );

  // Desktop → a compact popover anchored to the ⋯ button (drops down, right edge aligned to it;
  // flips above if it would overflow the bottom). Mobile/touch → the full-width bottom sheet.
  if (anchor && typeof window !== 'undefined') {
    const MENU_H = 300;   // +2 rows for cloud snapshot actions (#86bb7phhh)
    const dropDown = anchor.bottom + 6 + MENU_H <= window.innerHeight;
    // z-index MUST stay above the outside-click scrim below it (.live-sheet-scrim = z-index 80,
    // liveStyle.css). At 70 the transparent scrim painted OVER the whole popover, so every item click
    // (camera-info toggle, Assign, Clear) hit the scrim's onClose and just closed the menu — that was
    // David's "camera info toggle doesn't work" on desktop. #86bb7phhh (2026-08-16).
    const style = { position: 'fixed', right: Math.max(8, Math.round(window.innerWidth - anchor.right)), zIndex: 90 };
    if (dropDown) style.top = Math.round(anchor.bottom + 6);
    else style.bottom = Math.round(window.innerHeight - anchor.top + 6);
    return (
      <>
        <div className="live-sheet-scrim live-sheet-scrim--transparent" onClick={onClose} data-testid="tile-actions-scrim" />
        <div className="live-sheet live-tile-menu" role="dialog" aria-label={`Camera actions for slot ${slotNum}`} style={style}>
          {actions}
        </div>
      </>
    );
  }

  return (
    <Sheet
      className="live-sheet live-tile-sheet"
      scrimClassName="live-sheet-scrim"
      scrimTestId="tile-actions-scrim"
      ariaLabel={`Camera actions for slot ${slotNum}`}
      onClose={onClose}
    >
      {actions}
    </Sheet>
  );
};

export default TileActionsSheet;
